import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createItem, updateItem, getItemById } from "./PlannerService";
import { getAllAccounts } from "../expense/accounts/AccountService";
import CommentThread from "../shared/CommentThread";
import DependenciesPanel from "./DependenciesPanel";
import PriorityPanel from "./PriorityPanel";
import PriorityWeightsPanel from "./PriorityWeightsPanel";
import "./PlannerForm.css";

/* =====================================================
   IMPORTANT RULE (FIX APPLIED)
   -----------------------------------------------------
   priority is ALWAYS a STRING:
   "CRITICAL" | "HIGH" | "MEDIUM" | "LOW" | "SOMEDAY"
   ===================================================== */

export default function PlannerForm() {
  const nav = useNavigate();
  const { id } = useParams();

  /* ------------------ STATE ------------------ */

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [type, setType] = useState("TODO");
  const [status, setStatus] = useState("ACTIVE");

  const [category, setCategory] = useState("PERSONAL");
  const [projectName, setProjectName] = useState("");

  // 🔥 priority as STRING only
  const [priority, setPriority] = useState("MEDIUM");

  const [dueDate, setDueDate] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");

  const [recurrence, setRecurrence] = useState("NONE");
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("BILL");
  const [linkedAccountId, setLinkedAccountId] = useState("");
  const [totalEmiAmount, setTotalEmiAmount] = useState("");
  const [remainingEmiAmount, setRemainingEmiAmount] = useState("");
  const [emiStartDate, setEmiStartDate] = useState("");
  const [creditCardStatementAmount, setCreditCardStatementAmount] = useState("");
  const [creditCardMinDue, setCreditCardMinDue] = useState("");
  const [creditCardOutstandingAmount, setCreditCardOutstandingAmount] = useState("");
  const [creditCardStatementDate, setCreditCardStatementDate] = useState("");
  const [accounts, setAccounts] = useState([]);

  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderBeforeDays, setReminderBeforeDays] = useState(1);
  const [checklist, setChecklist] = useState([]);
  const [newCheckText, setNewCheckText] = useState("");
  const [estimatedDurationMinutes, setEstimatedDurationMinutes] = useState("");

  /* ------------------ LOAD EDIT ------------------ */

  useEffect(() => {
    if (id) loadItem();
  }, [id]);

  useEffect(() => {
    getAllAccounts()
      .then((data) => setAccounts(data || []))
      .catch(() => setAccounts([]));
  }, []);

  const loadItem = async () => {
    const item = await getItemById(id);
    if (!item) return;

    setTitle(item.title ?? "");
    setDescription(item.description ?? "");

    // TYPE mapping (kept as requested)
    if (item.type === "IMPORTANT_DATE") setType("EVENT");
    else if (item.type === "PAYMENT") setType("PAYMENT");
    else setType("TODO");

    // 🔥 SAFE priority normalization
    setPriority(
      typeof item.priority === "string" ? item.priority : "MEDIUM"
    );

    setDueDate(item.dueDate ?? "");
    setEventDate(item.eventDate ?? "");
    setPaymentDate(item.paymentDate ?? "");

    setRecurrence(item.recurrence ?? "NONE");
    setAmount(item.amount ?? "");
    setPaymentType(item.paymentType ?? "BILL");
    setLinkedAccountId(item.linkedAccountId ?? "");
    setTotalEmiAmount(item.totalEmiAmount ?? "");
    setRemainingEmiAmount(item.remainingEmiAmount ?? "");
    setEmiStartDate(item.emiStartDate ?? "");
    setCreditCardStatementAmount(item.creditCardStatementAmount ?? "");
    setCreditCardMinDue(item.creditCardMinDue ?? "");
    setCreditCardOutstandingAmount(item.creditCardOutstandingAmount ?? "");
    setCreditCardStatementDate(item.creditCardStatementDate ?? "");

    setReminderEnabled(item.reminderEnabled ?? false);
    setReminderBeforeDays(item.reminderBeforeDays ?? 1);
    setStatus(item.status ?? "ACTIVE");
    setCategory(item.category ?? "PERSONAL")
    setProjectName(item.projectName ?? "");
    setChecklist(item.checklistItems || []);
    setEstimatedDurationMinutes(item.estimatedDurationMinutes ?? "");
  };

  /* ------------------ BUILD PAYLOAD ------------------ */

  const buildPayload = () => {
    const payload = {
      title,
      description,
      priority,              // ✅ STRING ENUM (FIX)
      recurrence,
      reminderEnabled,
      reminderBeforeDays,
      status,
      category,
      projectName: projectName?.trim() ? projectName.trim() : null,
      checklistItems: checklist.length > 0 ? checklist : [],
      estimatedDurationMinutes: estimatedDurationMinutes !== "" ? Number(estimatedDurationMinutes) : null,
    };
    //console.log("PAYLOAD ->", payload);

    if (type === "TODO") {
      payload.type = "TODO";
      payload.dueDate = dueDate || null;
    }

    if (type === "REMINDER") {
      payload.type = "TODO";
      payload.dueDate = dueDate || null;
      payload.reminderEnabled = true;
    }

    if (type === "EVENT") {
      payload.type = "IMPORTANT_DATE";
      payload.eventDate = eventDate || null;
    }

    if (type === "PAYMENT") {
      payload.type = "PAYMENT";
      payload.paymentDate = paymentDate || null;
      payload.amount = amount ? Number(amount) : null;
      payload.paymentType = paymentType;
      payload.linkedAccountId = linkedAccountId || null;
      payload.totalEmiAmount = paymentType === "EMI" && totalEmiAmount ? Number(totalEmiAmount) : null;
      payload.remainingEmiAmount = paymentType === "EMI" && remainingEmiAmount ? Number(remainingEmiAmount) : null;
      payload.emiStartDate = paymentType === "EMI" ? (emiStartDate || null) : null;
      payload.creditCardStatementAmount = paymentType === "CREDIT_CARD" && creditCardStatementAmount ? Number(creditCardStatementAmount) : null;
      payload.creditCardMinDue = paymentType === "CREDIT_CARD" && creditCardMinDue ? Number(creditCardMinDue) : null;
      payload.creditCardOutstandingAmount = paymentType === "CREDIT_CARD" && creditCardOutstandingAmount ? Number(creditCardOutstandingAmount) : null;
      payload.creditCardStatementDate = paymentType === "CREDIT_CARD" ? (creditCardStatementDate || null) : null;
    }

    return payload;
  };

  /* ------------------ SUBMIT ------------------ */

  const submit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();

    if (id) await updateItem(id, payload);
    else await createItem(payload);

    nav("/planner");
  };

  /* ------------------ UI ------------------ */

  return (
    <div className="planner-form-page-wrap">
    <form className="planner-form-wrap" onSubmit={submit}>
      <h2>{id ? "Edit" : "Create"} Planner Item</h2>

      <Field label="Title">
        <input value={title} onChange={e => setTitle(e.target.value)} />
      </Field>

      <Field label="Type">
        <select value={type} onChange={e => setType(e.target.value)}>
          <option value="TODO">To-Do</option>
          <option value="REMINDER">Reminder</option>
          <option value="PAYMENT">Payment</option>
          <option value="EVENT">Important Date</option>
        </select>
      </Field>

      <Field label="Description">
        <textarea value={description} onChange={e => setDescription(e.target.value)} />
      </Field>

      <ChecklistEditor
        items={checklist}
        newText={newCheckText}
        onNewTextChange={setNewCheckText}
        onAdd={() => {
          const text = newCheckText.trim();
          if (!text) return;
          setChecklist(prev => [...prev, { id: crypto.randomUUID(), text, done: false }]);
          setNewCheckText("");
        }}
        onToggle={itemId => setChecklist(prev => prev.map(c => c.id === itemId ? { ...c, done: !c.done } : c))}
        onRemove={itemId => setChecklist(prev => prev.filter(c => c.id !== itemId))}
        onTextEdit={(itemId, text) => setChecklist(prev => prev.map(c => c.id === itemId ? { ...c, text } : c))}
      />

      <Field label="Category">
        <select value={category} onChange={e => setCategory(e.target.value)}>
          <option value="WORK">Work</option>
          <option value="PERSONAL">Personal</option>
          <option value="FINANCE">Finance</option>
          <option value="HEALTH">Health</option>
          <option value="FAMILY">Family</option>
          <option value="EDUCATION">Education</option>
          <option value="BUSINESS">Business</option>
          <option value="SHOPPING">Shopping</option>
          <option value="TRAVEL">Travel</option>
          <option value="BILLING">Billing</option>
          <option value="INVESTMENT">Investment</option>
          <option value="SUBSCRIPTION">Subscription</option>
          <option value="INSURANCE">Insurance</option>
          <option value="HOME">Home</option>
          <option value="SOCIAL">Social</option>
          <option value="GOALS">Goals</option>
          <option value="OTHER">Others</option>
          <option value="PROJECT">Project</option>
        </select>
      </Field>

      <Field label="Project">
        <input
          placeholder="e.g. Build Home"
          value={projectName}
          onChange={e => setProjectName(e.target.value)}
        />
      </Field>

      <Field label="Status">
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="ACTIVE">Active</option>
          <option value="IN_PROGRESS">In-Progress</option>
          <option value="TRACK">Track</option>
          <option value="DONE">Done</option>
          <option value="SKIPPED">Skipped</option>
          <option value="CANCELLED">Cancelled</option>
          <option value="TODO">ToDo</option>
        </select>
      </Field>

      <Field label="Priority">
        <select value={priority} onChange={e => setPriority(e.target.value)}>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
          <option value="SOMEDAY">Someday</option>
        </select>
      </Field>

      <Field label="Estimated duration (minutes)">
        <input
          type="number"
          min="0"
          placeholder="e.g. 30 — feeds the Priority Engine's Effort factor"
          value={estimatedDurationMinutes}
          onChange={e => setEstimatedDurationMinutes(e.target.value)}
        />
      </Field>

      {type === "TODO" && (
        <>
          <Field label="Due Date">
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </Field>
          <Recurrence recurrence={recurrence} setRecurrence={setRecurrence} />
          <Reminder {...{ reminderEnabled, setReminderEnabled, reminderBeforeDays, setReminderBeforeDays }} />
        </>
      )}

      {type === "EVENT" && (
        <>
          <Field label="Event Date">
            <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)} />
          </Field>
          <Recurrence recurrence={recurrence} setRecurrence={setRecurrence} />
        </>
      )}

      {type === "PAYMENT" && (
        <>
          <Field label="Payment Date">
            <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
          </Field>

          <Field label="Amount">
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} />
          </Field>

          <Field label="Payment Type">
            <select value={paymentType} onChange={e => setPaymentType(e.target.value)}>
              <option value="RENT">Rent</option>
              <option value="EMI">EMI</option>
              <option value="CREDIT_CARD">Credit Card</option>
              <option value="SIP">SIP</option>
              <option value="SUBSCRIPTION">Subscription</option>
              <option value="BILL">Bill</option>
              <option value="INSURANCE">Insurance</option>
            </select>
          </Field>

          <Field label="Linked Account">
            <select value={linkedAccountId} onChange={e => setLinkedAccountId(e.target.value)}>
              <option value="">Select account</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {(account.accountName || account.id) +
                    (account.accountNumber ? ` • ${String(account.accountNumber).slice(-4)}` : "")}
                </option>
              ))}
            </select>
          </Field>

          {paymentType === "EMI" && (
            <>
              <Field label="Total EMI Amount">
                <input
                  type="number"
                  value={totalEmiAmount}
                  onChange={e => setTotalEmiAmount(e.target.value)}
                />
              </Field>
              <Field label="Current Remaining Amount">
                <input
                  type="number"
                  value={remainingEmiAmount}
                  onChange={e => setRemainingEmiAmount(e.target.value)}
                />
              </Field>
              <Field label="EMI Start Date">
                <input
                  type="date"
                  value={emiStartDate}
                  onChange={e => setEmiStartDate(e.target.value)}
                />
              </Field>
            </>
          )}

          {paymentType === "CREDIT_CARD" && (
            <>
              <Field label="Statement Amount">
                <input
                  type="number"
                  value={creditCardStatementAmount}
                  onChange={e => setCreditCardStatementAmount(e.target.value)}
                />
              </Field>
              <Field label="Minimum Due">
                <input
                  type="number"
                  value={creditCardMinDue}
                  onChange={e => setCreditCardMinDue(e.target.value)}
                />
              </Field>
              <Field label="Outstanding Amount">
                <input
                  type="number"
                  value={creditCardOutstandingAmount}
                  onChange={e => setCreditCardOutstandingAmount(e.target.value)}
                />
              </Field>
              <Field label="Statement Date">
                <input
                  type="date"
                  value={creditCardStatementDate}
                  onChange={e => setCreditCardStatementDate(e.target.value)}
                />
              </Field>
            </>
          )}

          <Recurrence recurrence={recurrence} setRecurrence={setRecurrence} />
          <Reminder {...{ reminderEnabled, setReminderEnabled, reminderBeforeDays, setReminderBeforeDays }} />
        </>
      )}

      <button type="submit">Save</button>
    </form>

      {id && (
        <div className="planner-form-field-wrap">
          <label className="planner-form-label">Smart Priority</label>
          <PriorityPanel itemId={id} />
          <PriorityWeightsPanel />
        </div>
      )}

      {id && (
        <div className="planner-form-field-wrap">
          <label className="planner-form-label">Dependencies</label>
          <DependenciesPanel itemId={id} />
        </div>
      )}

      {id && (
        <div className="planner-form-field-wrap">
          <label className="planner-form-label">Comments</label>
          <CommentThread entityType="TASK" entityId={id} />
        </div>
      )}
    </div>
  );
}

/* ------------------ REUSABLE COMPONENTS ------------------ */

function Field({ label, children }) {
  return (
    <div className="planner-form-field-wrap">
      <label className="planner-form-label">{label}</label>
      {children}
    </div>
  );
}

function Recurrence({ recurrence, setRecurrence }) {
  return (
    <Field label="Recurrence">
      <select value={recurrence} onChange={e => setRecurrence(e.target.value)}>
        <option value="NONE">None</option>
        <option value="DAILY">Daily</option>
        <option value="WEEKDAYS">Weekdays (Mon–Fri)</option>
        <option value="WEEKLY">Weekly</option>
        <option value="MONTHLY">Monthly</option>
        <option value="HALF_YEARLY">Half-Yearly</option>
        <option value="YEARLY">Yearly</option>
      </select>
    </Field>
  );
}

function Reminder({ reminderEnabled, setReminderEnabled, reminderBeforeDays, setReminderBeforeDays }) {
  return (
    <Field label="Reminder">
      <label>
        <input
          type="checkbox"
          checked={reminderEnabled}
          onChange={e => setReminderEnabled(e.target.checked)}
        />
        Enable
      </label>

      {reminderEnabled && (
        <input
          type="number"
          min="1"
          value={reminderBeforeDays}
          onChange={e => setReminderBeforeDays(Number(e.target.value))}
        />
      )}
    </Field>
  );
}

function ChecklistEditor({ items, newText, onNewTextChange, onAdd, onToggle, onRemove, onTextEdit }) {
  const inputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); onAdd(); }
  };

  return (
    <div className="planner-checklist-editor">
      <div className="planner-checklist-label">
        Checklist
        {items.length > 0 && (
          <span className="planner-checklist-count">
            {items.filter(c => c.done).length}/{items.length}
          </span>
        )}
      </div>
      <ul className="planner-checklist-items">
        {items.map(item => (
          <li key={item.id} className="planner-checklist-item">
            <input
              type="checkbox"
              checked={item.done}
              onChange={() => onToggle(item.id)}
              className="planner-check-box"
            />
            <input
              className={`planner-check-text ${item.done ? "planner-check-done" : ""}`}
              value={item.text}
              onChange={e => onTextEdit(item.id, e.target.value)}
            />
            <button type="button" className="planner-check-remove" onClick={() => onRemove(item.id)}>✕</button>
          </li>
        ))}
      </ul>
      <div className="planner-checklist-add-row">
        <input
          ref={inputRef}
          className="planner-check-new-input"
          value={newText}
          onChange={e => onNewTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add step…"
        />
        <button type="button" className="planner-check-add-btn" onClick={onAdd}>+</button>
      </div>
    </div>
  );
}
